import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { subscriptionAPI } from "../lib/api";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Check, Crown, Zap, Clock, FileText, Users, TrendingUp, LogOut } from "lucide-react";

export default function PaywallPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // TEMPORARY QA: ?preview=1 bypasses the trial-active redirect so billing UI/payment
  // flows can be tested without waiting for the trial to expire.
  // This does NOT affect subscription state or trial expiry for the user.
  const isPreviewMode = searchParams.get("preview") === "1";
  const { user, logout, refreshSubscription, isSubscriptionActive, isAdmin } = useAuth();

  const [paypalConfig, setPaypalConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  // ⭐ Free trial logic (comes from backend config)
  const isInFreeTrial = paypalConfig?.account_age_days != null && paypalConfig.account_age_days <= 10;
  const hasAccess = isSubscriptionActive || isInFreeTrial;

  // ⭐ Fetch PayPal config ONCE on mount
  useEffect(() => {
    fetchConfig();
  }, []);

  // ⭐ Redirect AFTER config is loaded
  useEffect(() => {
    if (loading) return; // wait for config

    // Admins always skip paywall
    if (isAdmin) {
      navigate("/dashboard");
      return;
    }

    // Free trial OR active subscription → skip paywall (unless in QA preview mode)
    if (hasAccess && !isPreviewMode) {
      navigate("/dashboard");
      return;
    }

  }, [loading, isAdmin, hasAccess, isPreviewMode, navigate]);

  const fetchConfig = async () => {
    try {
      const res = await subscriptionAPI.getConfig();
      setPaypalConfig(res.data);
    } catch (err) {
      console.error("Failed to fetch PayPal config:", err);
      toast.error("Failed to load payment options");
    } finally {
      setLoading(false);
    }
  };


  const handleSubscriptionApprove = async (data, actions) => {
    setProcessing(true);
    console.log("[PayPal] onApprove fired. subscriptionID:", data.subscriptionID, "orderID:", data.orderID);
    try {
      // Activate subscription in our backend
      console.log("[PayPal] Sending subscriptionID to /subscription/activate...");
      const res = await subscriptionAPI.activate({
        paypal_subscription_id: data.subscriptionID,
      });
      console.log("[PayPal] /subscription/activate response:", res.data);

      if (res.data.success) {
        toast.success("Subscription activated! Welcome to FreelanceTrackIt Pro!");
        // Refresh subscription status
        console.log("[PayPal] Refreshing subscription state...");
        const newSub = await refreshSubscription();
        console.log("[PayPal] Refreshed subscription:", newSub);
        navigate("/dashboard");
      } else {
        console.warn("[PayPal] activate returned success=false:", res.data);
        toast.error("Failed to activate subscription");
      }
    } catch (err) {
      console.error("Subscription activation error:", err, err.response?.data);
      toast.error(err.response?.data?.detail || "Failed to activate subscription");
    } finally {
      setProcessing(false);
    }
  };

  const benefits = [
    { icon: Users, text: "Unlimited Clients & Projects" },
    { icon: Clock, text: "Time Tracking & Billing" },
    { icon: FileText, text: "Professional Invoices & Quotes" },
    { icon: TrendingUp, text: "Dashboard Analytics" },
    { icon: Zap, text: "Recurring Invoices" },
    { icon: Crown, text: "Financial Reports" },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4" data-testid="paywall-page">
      <div className="w-full max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-blue-500/10 text-blue-400 px-4 py-2 rounded-full mb-4">
            <Crown className="h-4 w-4" />
            <span className="text-sm font-medium">Subscription Required</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Unlock FreelanceTrackIt
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Manage your freelance business with powerful tools for time tracking, invoicing, and client management.
          </p>
        </div>

        {/* Pricing Card */}
        <Card className="bg-gray-800/50 border-gray-700 backdrop-blur-sm max-w-md mx-auto">
          <CardHeader className="text-center pb-4">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-500/10 rounded-2xl mb-4 mx-auto">
              <Zap className="h-8 w-8 text-blue-400" />
            </div>
            <CardTitle className="text-2xl text-white">Pro Plan</CardTitle>
            <CardDescription className="text-gray-400">
              Everything you need to run your freelance business
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Price */}
            <div className="text-center">
              <div className="flex items-baseline justify-center gap-1">
                <span className="text-5xl font-bold text-white">₱699</span>
                <span className="text-gray-400">/month</span>
              </div>
              <div className="text-sm text-gray-400 mt-2">Full workspace access for 10 days. Then ₱699/month.</div>
              {paypalConfig?.paypal_plan_id && (
                <div className="text-xs text-muted-foreground mt-1">Plan ID: {paypalConfig.paypal_plan_id}</div>
              )}
            </div>

            {/* Benefits */}
            <div className="space-y-3">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center gap-3 text-gray-300">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-500/10 flex items-center justify-center">
                    <Check className="h-3 w-3 text-green-400" />
                  </div>
                  <span>{benefit.text}</span>
                </div>
              ))}
            </div>

            {/* PayPal Button */}
            <div className="pt-4">
              {paypalConfig?.paypal_client_id && paypalConfig?.paypal_plan_id ? (
                <PayPalScriptProvider
                  options={{
                    clientId: paypalConfig.paypal_client_id,
                    vault: true,
                    intent: "subscription",
                  }}
                >
                  <PayPalButtons
                    style={{
                      shape: "rect",
                      color: "gold",
                      layout: "vertical",
                      label: "subscribe",
                    }}
                    createSubscription={(data, actions) => {
                      console.log("[PayPal] createSubscription called with plan_id:", paypalConfig.paypal_plan_id);
                      return actions.subscription.create({
                        plan_id: paypalConfig.paypal_plan_id,
                      });
                    }}
                    onApprove={handleSubscriptionApprove}
                    onError={(err) => {
                      console.error("PayPal error:", err);
                      toast.error("Payment failed. Please try again.");
                    }}
                  />
                </PayPalScriptProvider>
              ) : paypalConfig?.paypal_client_id ? (
                <div className="text-center space-y-4">
                  <div className="p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                    <p className="text-yellow-400 text-sm">
                      PayPal subscription plan is being configured. Please check back shortly or contact the administrator.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center space-y-4">
                  <div className="p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                    <p className="text-yellow-400 text-sm">
                      PayPal is not configured. Please contact the administrator to set up payment processing.
                    </p>
                  </div>
                  {/* Demo mode - allow bypassing for testing */}
                  <Button
                    onClick={async () => {
                      setProcessing(true);
                      try {
                        // For demo/testing - activate without PayPal
                        await subscriptionAPI.activate({
                          paypal_subscription_id: `demo_${Date.now()}`,
                        });
                        toast.success("Demo subscription activated!");
                        await refreshSubscription();
                        navigate("/dashboard");
                      } catch (err) {
                        toast.error("Activation failed");
                      } finally {
                        setProcessing(false);
                      }
                    }}
                    disabled={processing}
                    className="w-full"
                    data-testid="demo-subscribe-btn"
                  >
                    {processing ? "Activating..." : "Activate Demo Subscription"}
                  </Button>
                </div>
              )}
            </div>

            {processing && (
              <div className="text-center text-gray-400">
                <div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full mx-auto mb-2" />
                Processing your subscription...
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-8 space-y-4">
          <p className="text-gray-500 text-sm">
            Secure payment powered by PayPal • Cancel anytime
          </p>
          {user && (
            <div className="flex items-center justify-center gap-4">
              <span className="text-gray-400 text-sm">Logged in as {user.email}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={logout}
                className="text-gray-400 hover:text-white"
                data-testid="logout-btn"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
